<template>
    <div class="ppv2-space-y-2">
        <!-- Label principal -->
        <BaseLabel v-if="label" :label="label" :for-id="inputId" :required="required" />

        <InfoMessageBox v-if="tooltipMsg" class="ppv2-mt-2" :message="tooltipMsg" color="info" />

        <!-- Description -->
        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500">{{ description }}</p>

        <!-- Liste des options -->
        <div
            :class="{
                'ppv2-flex ppv2-flex-col ppv2-space-y-4': !inline,
                'ppv2-flex ppv2-flex-row ppv2-space-x-4': inline,
            }"
        >
            <label
                v-for="option in options"
                :key="option.value"
                class="ppv2-flex ppv2-flex-col ppv2-cursor-pointer ppv2-space-y-1"
                :class="{ 'ppv2-opacity-60 ppv2-cursor-not-allowed': disabled }"
            >
                <div class="ppv2-flex ppv2-items-center ppv2-space-x-2">
                    <div class="ppv2-relative">
                        <input
                            type="radio"
                            :value="normalizeBoolean(option.value)"
                            v-model="internalValue"
                            :disabled="disabled"
                            class="ppv2-sr-only"
                        />
                        <div
                            :class="[
                                'ppv2-rounded-full ppv2-border ppv2-transition-all ppv2-ease-in-out',
                                sizeClass,
                                error
                                    ? 'ppv2-border-red-500'
                                    : normalizeBoolean(internalValue) === normalizeBoolean(option.value)
                                      ? 'ppv2-border-black'
                                      : 'ppv2-border-black',
                                disabled ? 'ppv2-bg-gray-100 ppv2-border-gray-300' : '',
                                !disabled
                                    ? 'ppv2-hover:border-primary-500 ppv2-focus:ring-2 ppv2-focus:ring-primary-500 ppv2-focus:ring-opacity-50'
                                    : '',
                            ]"
                        >
                            <div
                                v-if="normalizeBoolean(internalValue) === normalizeBoolean(option.value)"
                                class="ppv2-absolute ppv2-top-1/2 ppv2-left-1/2 ppv2-w-2 ppv2-h-2 ppv2-bg-black ppv2-rounded-full ppv2-transform ppv2--translate-x-1/2 ppv2--translate-y-1/2"
                            ></div>
                        </div>
                    </div>
                    <span class="ppv2-text-black ppv2-text-sm">{{ option.label }}</span>
                </div>

                <!-- Description String -->
                <p
                    v-if="option.description && typeof option.description === 'string'"
                    class="ppv2-ml-6 ppv2-text-xs ppv2-text-black ppv2-text-opacity-60"
                >
                    {{ option.description }}
                </p>

                <!-- Description Array -->
                <ul
                    v-if="Array.isArray(option.description)"
                    class="ppv2-ml-6 ppv2-mt-1 ppv2-text-black ppv2-text-opacity-60 ppv2-space-y-1 ppv2-text-xs"
                >
                    <li v-for="(item, idx) in option.description" :key="`desc-${option.value}-${idx}`">&bull; {{ $t(item) }}</li>
                </ul>
            </label>
        </div>

        <!-- Message d'erreur -->
        <p v-if="error" class="ppv2-text-xs ppv2-text-[#ed4b9e]">{{ error }}</p>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import InfoMessageBox from './InfoMessageBox.vue'

export default {
    name: 'BaseRadioGroup',
    components: {
        BaseLabel,
        InfoMessageBox,
    },
    props: {
        value: [String, Boolean, Number],
        options: {
            type: Array,
            required: true,
        },
        label: {
            type: String,
            required: false,
        },
        description: String,
        required: Boolean,
        disabled: Boolean,
        error: String,
        size: {
            type: String,
            default: 'md',
            validator: (val) => ['sm', 'md', 'lg'].includes(val),
        },
        tooltipMsg: {
            type: String,
            default: '',
        },
        inline: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            internalValue: this.normalizeBoolean(this.value),
            internalInputId: `input-${Math.random().toString(36).substr(2, 9)}`,
        }
    },
    watch: {
        value(val) {
            this.internalValue = this.normalizeBoolean(val)
        },
        internalValue(val) {
            this.$emit('input', this.normalizeBoolean(val))
        },
    },
    computed: {
        inputId() {
            return this.internalInputId
        },
        sizeClass() {
            switch (this.size) {
                case 'sm':
                    return 'ppv2-w-[14px] ppv2-h-[14px]'
                case 'lg':
                    return 'ppv2-w-[22px] ppv2-h-[22px]'
                default:
                    return 'ppv2-w-[18px] ppv2-h-[18px]'
            }
        },
    },
    methods: {
        normalizeBoolean(val) {
            if (val === true || val === 'true' || val === 1 || val === '1') return true
            if (val === false || val === 'false' || val === 0 || val === '0') return false
            return val
        },
    },
}
</script>

<style scoped></style>
